import { Flex } from '@bridebook/aura';
import { Container } from '@bridebook/ui';
import FooterBottom from '@bridebook/web/app-shared/components/footer/footer-bottom/footer-bottom';
import { GetWeddingWebsiteDataResponseError } from '@bridebook/web/pages/api/for/website/[websiteId]';
import { Trans, useTranslation } from 'next-i18next';
import { LayoutWrapper } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/components/layout-wrapper';
import {
  Header1,
  Header3,
} from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/components/typography';
import { ThemeProvider } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/theme-provider';
import {
  toValidFontPair,
  toValidPalette,
} from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/utils';
import { PasswordForm } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/password-page/password-form';

type Props = {
  websiteData: GetWeddingWebsiteDataResponseError;
  isFetching: boolean;
  onPasswordEntered: (pin: string) => void;
};

export const PasswordPage = ({ websiteData, isFetching, onPasswordEntered }: Props) => {
  const { t } = useTranslation('weddingWebsite');

  // names
  const missingPartnerName = t('partners.missingNo');
  const displayFirstName = websiteData.wedding.partners[0] || missingPartnerName;
  const displaySecondName = websiteData.wedding.partners[1] || missingPartnerName;

  const palette = toValidPalette(websiteData.website.config.theme.palette);
  const fontPair = toValidFontPair(websiteData.website.config.theme.font);

  return (
    <>
      <div className="flex h-screen w-full flex-col">
        <ThemeProvider
          palette={palette}
          fontPair={fontPair}
          preloadAllFonts
          className="@container h-full w-full">
          <LayoutWrapper className="h-full justify-center" underAccent={false} overAccent={false}>
            <Flex className="w-full flex-col items-center justify-center gap-16 p-10 pt-8 pb-28 @screen-lg:gap-20">
              <Flex className="flex-col items-center gap-2 @screen-lg:gap-6">
                <Trans
                  ns="weddingWebsite"
                  i18nKey="passwordPage.headings"
                  values={{
                    firstPartnerName: displayFirstName,
                    secondPartnerName: displaySecondName,
                  }}
                  components={{
                    small: <Header3 />,
                    large: <Header1 />,
                    br: <br />,
                  }}
                />
              </Flex>

              <Flex className="flex-col items-center gap-4">
                <InputCta>{t('previewMode.password.tip')}</InputCta>
                <PasswordForm
                  onPasswordEntered={onPasswordEntered}
                  isInvalidPinError={websiteData.resultCode === 'invalid_pin'}
                  isFetching={isFetching}
                />
              </Flex>
            </Flex>
          </LayoutWrapper>
        </ThemeProvider>
        <div className="z-100 bg-white">
          <Container>
            <FooterBottom localeSelect={null} />
          </Container>
        </div>
      </div>
    </>
  );
};

const InputCta = ({ children }: { children: React.ReactNode }) => (
  <p className="m-0 text-center font-[family-name:var(--font-body-typeface)] text-base text-[var(--ww-palette-text-secondary)]">
    {children}
  </p>
);
